import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { confirmLeave, hasUnsavedChanges } from '@/composables/useUnsavedGuard'
import { homeRouteName } from '@/router/navigation-menu'
import { consumeNavigationFlags, markBooted, setPendingNavigation } from '@/router/navigation-state'
import { SECTIONS } from '@/router/sections'
import { activePositions, can } from '@/services/rbac'
import { useAuthStore } from '@/stores/auth'
import { WORKSPACE_BY_ROUTE, WORKSPACES } from '@/workspaces/registry'
import type { CapabilityVerb } from '@/services/rbac'

// Флаги направления (popstate / in-app back) — в @/router/navigation-state,
// leaf-модуле без импортов: composables берут markInAppBack оттуда и не
// создают цикл с динамическими импортами страниц ниже.
export { markInAppBack } from '@/router/navigation-state'

// Разделы CRM и их карточки `/:id` — генерятся циклом из реестра SECTIONS, не руками
// по одному. Карточка — тот же компонент и тот же splitKey (≥ lg split с предвыбранной
// сущностью, ниже порога поэкранная карточка): выбор строки меняет URL без ремаунта.
const sectionRoutes = SECTIONS.flatMap((section) => {
  const shared = {
    titleKey: section.titleKey,
    capability: section.capability,
    splitKey: section.splitKey,
  }
  const sectionRoute = {
    path: section.path.replace(/^\//, ''),
    name: section.name,
    component: section.component,
    meta: { depth: 0, ...shared },
  }
  if (section.card === undefined) return [sectionRoute]
  return [
    sectionRoute,
    {
      path: section.card.path.replace(/^\//, ''),
      name: section.card.name,
      component: section.component,
      meta: { depth: 1, ...shared },
    },
  ]
})

// Десктопные рабочие места ролей (E19) — генерятся циклом из реестра WORKSPACES,
// не руками по одному. meta.workspace ловит guard (доступ по роли + mobile-редирект).
const workspaceRoutes = WORKSPACES.map((workspace) => ({
  path: workspace.path.replace(/^\//, ''),
  name: workspace.routeName,
  component: workspace.component,
  meta: { depth: 0, titleKey: workspace.titleKey, workspace: true },
}))

// Глубина иерархии роутов. Направление перехода вычисляется сравнением depth:
// to.depth > from.depth → forward (слайд справа налево)
// to.depth < from.depth → back    (слайд слева направо)
// Равные depth → направление определяется popstate-флагом.
const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { public: true, depth: 0 },
  },
  {
    path: '/two-factor',
    name: 'two-factor',
    component: () => import('@/pages/auth/TwoFactorPage.vue'),
    meta: { public: true, depth: 0 },
  },
  {
    path: '/recover',
    name: 'recover',
    component: () => import('@/pages/auth/RecoveryPage.vue'),
    meta: { public: true, depth: 0 },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { public: true, depth: 0 },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      {
        // Точка входа без своей страницы: редирект на домашний раздел роли
        // (рабочий экран под роль — см. homeRouteName).
        path: '',
        name: 'home',
        // Раздел роли выбирает beforeEach (homeRouteName) ПОСЛЕ инициализации auth —
        // нельзя синхронным route-level redirect'ом: он отрабатывает до auth-гарда,
        // и на холодной загрузке (refresh / прямой заход на «/») user ещё null
        // (fetchMe идёт в beforeEach), из-за чего залогиненного уводило на /login.
        // Компонент-заглушка не рендерится: beforeEach всегда уводит «/» в раздел
        // роли (или анонима — на /login с next=/).
        component: { render: () => null },
        meta: { depth: 0 },
      },
      ...sectionRoutes,
      {
        // Inbox колокольчика (E12, §4.8) — вход с bell-иконки шапки, не раздел навигации.
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/pages/NotificationsPage.vue'),
        meta: { depth: 1, titleKey: 'notifications.title' },
      },
      ...workspaceRoutes,
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  // Незавершённая навигация к `to` — таб-бар покажет лоадер на этом пункте, пока
  // грузится страница (lazy-chunk). Сбрасывается в afterEach/onError ниже.
  setPendingNavigation(to.path)

  const toDepth = (to.meta.depth as number | undefined) ?? 0
  const fromDepth = (from.meta.depth as number | undefined) ?? 0
  const { popstate, inAppBack } = consumeNavigationFlags()

  // Первый переход внутри SPA (from.name есть) → дальше скелетоны загрузки не
  // показываем: cold start с пустым from.name остаётся «холодным» (скелетон ок).
  if (from.name !== undefined) markBooted()

  // Универсальный гард несохранённых данных: формы регистрируют dirty-чекеры
  // через useUnsavedGuard — уход со страницы требует подтверждения юзера.
  // from.name undefined — первичная навигация (загрузка приложения), не гардим.
  if (from.name !== undefined && hasUnsavedChanges()) {
    const leave = await confirmLeave()
    if (!leave) return false
  }

  // Popstate без in-app флага — iOS edge-swipe или кнопка «Назад» браузера.
  // Движок уже сам визуально перелистнул страницу, свою анимацию не
  // накладываем — иначе видно двойное движение. Popstate с флагом —
  // это router.back() от нашей кнопки «← Сегодня» и подобных: жеста не
  // было, анимация уместна (направление — по depth, иначе slide-right).
  let transition: string
  if (popstate && !inAppBack) {
    transition = 'page-instant'
  } else if (toDepth !== fromDepth) {
    transition = toDepth < fromDepth ? 'page-slide-right' : 'page-slide-left'
  } else if (inAppBack) {
    transition = 'page-slide-right'
  } else {
    transition = 'page-slide-left'
  }
  to.meta.transition = transition

  const auth = useAuthStore()
  if (!auth.initialized) await auth.fetchMe()
  if (to.meta.public) return true
  if (!auth.user) return { name: 'login', query: { next: to.fullPath } }

  // «/» страницы не имеет — после инициализации auth отправляем в домашний экран
  // роли (рабочее место, если есть; иначе поэкранный раздел — homeRouteName,
  // одинаково на десктопе и мобилке: рабочие места адаптивны).
  if (to.name === 'home') return { name: homeRouteName(auth.capabilities) }

  // RBAC-гард разделов (ТЗ §3.3 п.3) — ОДНА проверка: право из реестра (meta.capability)
  // против served прав сессии; служебный superadmin приходит с сервера со всеми verb'ами,
  // ручного байпаса тут нет. Это только UX, реальный доступ режут политики API (403).
  const capability = to.meta.capability as CapabilityVerb | undefined
  if (capability !== undefined && !can(auth.capabilities, capability)) {
    return { name: 'home' }
  }

  // Рабочие места ролей (E19): адаптивны (десктоп — split, мобилка — поэкранно),
  // доступны на любом экране. Доступ — только при активной позиции роли этого
  // места; чужой workspace → свой домашний экран.
  // Место — не capability-verb, а «своя ли это роль», поэтому сверяем позиции; у
  // служебного аккаунта их нет — его пускает served-флаг superadmin.
  if (to.meta.workspace === true) {
    const workspace = WORKSPACE_BY_ROUTE[to.name as string]
    const owns =
      auth.capabilities?.superadmin === true ||
      (workspace !== undefined &&
        activePositions(auth.user.positions).some((position) => position.role === workspace.role))
    if (!owns) return { name: homeRouteName(auth.capabilities) }
  }
  return true
})

// Навигация завершилась (успех, отмена или редирект-цепочка) — гасим лоадер
// таб-бара. afterEach вызывается и при aborted-навигации (третий аргумент —
// failure), onError — при падении загрузки lazy-chunk'а: оба сбрасывают pending.
router.afterEach(() => setPendingNavigation(null))
router.onError(() => setPendingNavigation(null))
